/**
 * Page de connexion. Formulaire email/password.
 * Affiche le logo du club et le titre configure.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Waves, LogIn, AlertCircle, ExternalLink, KeyRound } from 'lucide-react';
import axios from 'axios';
import { memoriserDestination } from '../lib/utils';
import { setFavicon } from '../lib/utils';
import MaintenancePage from './MaintenancePage';
import { useClassesTheme } from '../lib/theme';
import FondAnime from '../components/commun/FondAnime';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const APP_VERSION = "v1.81.3.0";

// 05/09/2026 (demande club "qrcode d'acces a la fiche de formation") : ProtectedRoute (App.js)
// pose ?redirect=... quand une page protegee est visitee sans etre connecte, pour y revenir
// une fois connecte au lieu de toujours repartir sur "/". Garde-fou open-redirect : n'accepte
// qu'un chemin interne commencant par un seul "/" - "//evil.com" ou une URL absolue seraient
// traites par le navigateur comme une redirection externe si on les passait tels quels a une
// navigation, meme si React Router lui-meme ne fait que du routage interne.
const sanitizeRedirect = (raw) => (raw && raw.startsWith('/') && !raw.startsWith('//')) ? raw : '/';
const getSafeRedirect = () => sanitizeRedirect(new URLSearchParams(window.location.search).get('redirect'));

// 02/10/2026 (demande club) : une licence seche (licence FFESSM seule) ne donne pas acces a
// l'espace adherent - le backend refuse la connexion (403 LICENCE_SECHE / ?error=licence_seche).
const MESSAGE_LICENCE_SECHE = "Votre adhésion est une licence sèche : elle ne donne pas accès à l'espace adhérent. Pour toute question, écrivez au secrétariat.";

const LoginPage = () => {
  // 1.46.5.0 (refonte, lot 6) : page hors du Layout - elle applique elle-meme le theme choisi sur cet appareil.
  useClassesTheme(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(''); // 'auth' | 'db' | 'network'
  const [loading, setLoading] = useState(false);
  const [clubInfo, setClubInfo] = useState({});
  const [healthStatus, setHealthStatus] = useState(null);
  // Formulaire classique masque par defaut en production quand le SSO WordPress est
  // disponible (bascule via le lien "Se connecter avec un mot de passe" ci-dessous) - reste
  // toujours accessible, jamais retire completement, en filet de securite si le SSO tombe en
  // panne. En mode test, le formulaire reste affiche directement (inchange).
  const [showClassicForm, setShowClassicForm] = useState(false);
  // 17/09/2026 : sortie de secours du mode maintenance. Memorisee pour la session d'onglet,
  // sinon le moindre rechargement (ou la redirection du flux SSO) renverrait l'administrateur
  // sur la page de maintenance juste avant qu'il puisse la desactiver. sessionStorage peut
  // lever (navigation privee, cookies bloques) : l'echec ne doit pas casser la page de login.
  const [bypassMaintenance, setBypassMaintenance] = useState(() => {
    try { return sessionStorage.getItem('cesam_maintenance_bypass') === '1'; } catch { return false; }
  });
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // SSO: si un code d'echange a usage unique est dans l'URL (?sso_code=xxx).
    // Le JWT de session n'est jamais transmis en query string (il finirait en clair
    // dans les logs d'acces) : ce code, lui, est echange contre le vrai JWT via un
    // POST classique, puis invalide immediatement cote serveur.
    const params = new URLSearchParams(window.location.search);
    const ssoCode = params.get('sso_code');
    const ssoError = params.get('error');
    if (ssoError === 'subscription_expired') {
      // Compte adherent (sans role staff) sans adhesion validee pour la saison en cours :
      // sso_wp_redirect bloque avant meme de generer un code d'echange (cf. backend
      // is_login_blocked_lapsed_adherent) - on redirige vers la page de renouvellement
      // plutot que d'afficher une simple erreur sur la mire de connexion.
      window.history.replaceState({}, '', window.location.pathname);
      navigate('/adhesion-expiree');
    } else if (ssoError) {
      const errorMessages = {
        'sso_not_configured': 'SSO non configure. Contactez l\'administrateur.',
        'sso_invalid_token': 'Token SSO invalide ou expire.',
        'sso_no_email': 'Email manquant dans le token SSO.',
        'sso_user_not_found': 'Aucun compte CESAM associe a cet email WordPress.',
        // 02/10/2026 : licence seche, sans acces a l'espace adherent (cf. backend LICENCE_SECHE).
        'licence_seche': MESSAGE_LICENCE_SECHE,
      };
      setError(errorMessages[ssoError] || 'Erreur SSO');
      setErrorType('auth');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (ssoCode) {
      // Capture AVANT le replaceState ci-dessous, qui efface toute la query string (donc aussi
      // ?redirect=... s'il etait present, pose par ProtectedRoute pour un scan QR a froid).
      const redirectTarget = sanitizeRedirect(params.get('redirect'));
      // 03/10/2026 : memorisee pour la protection des pages publiques (App.jsx), qui sinon
      // renvoyait vers "/" au moment ou la session s'ouvre.
      memoriserDestination(redirectTarget);
      // Nettoyer l'URL tout de suite : le code est a usage unique, inutile de le laisser trainer.
      window.history.replaceState({}, '', window.location.pathname);
      axios.post(`${BACKEND_URL}/api/auth/sso-wp/exchange`, { code: ssoCode })
        .then(res => {
          loginWithToken(res.data.token, res.data.user);
          navigate(redirectTarget);
        })
        .catch(() => {
          setError('Code SSO invalide ou expire. Reessayez depuis WordPress.');
          setErrorType('auth');
        });
    }
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/club-info`)
      .then(res => {
        setClubInfo(res.data || {});
        if (res.data?.app_title) document.title = res.data.app_title;
        if (res.data?.logo_url) setFavicon(`${BACKEND_URL}${res.data.logo_url}`);
      })
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/health`)
      .then(res => setHealthStatus(res.data))
      .catch(() => setHealthStatus({ status: 'error', database: 'disconnected', error: 'Backend inaccessible' }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorType('');
    setLoading(true);
    try {
      memoriserDestination(getSafeRedirect());
      await login(email, password);
      navigate(getSafeRedirect());
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403 && detail === 'ADHESION_EXPIRED') {
        navigate('/adhesion-expiree');
        return;
      }
      if (status === 403 && detail === 'LICENCE_SECHE') {
        setError(MESSAGE_LICENCE_SECHE);
        setErrorType('auth');
        return;
      }
      if (status === 429) {
        // Anti brute-force (v1.19.9.4) - message dedie pour eviter le sous-texte "network"
        // ("le serveur ne repond pas"), trompeur ici : le serveur repond, il limite juste.
        setError(detail || 'Trop de tentatives. Réessayez dans quelques minutes.');
        setErrorType('auth');
      } else if (status === 503) {
        setError(detail || 'Base de donnees inaccessible');
        setErrorType('db');
      } else if (status === 401) {
        setError(detail || 'Identifiants incorrects');
        setErrorType('auth');
      } else if (!err.response) {
        setError('Impossible de contacter le serveur');
        setErrorType('network');
      } else {
        setError(detail || 'Erreur de connexion');
        setErrorType('network');
      }
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = clubInfo.logo_url ? `${BACKEND_URL}${clubInfo.logo_url}` : null;
  const clubName = clubInfo.club_name || 'CESAM Plongee';
  const isTestMode = (clubInfo.app_mode || 'production') === 'test';
  // admin-ajax.php plutot que /wp-json/ (route REST) : WordPress exige un jeton nonce anti-CSRF
  // pour toute route REST appelee avec un cookie de connexion valide, impossible a fournir
  // depuis une simple navigation - la route REST fonctionnait donc uniquement deconnecte.
  // 05/09/2026 : ?redirect=... (pose par ProtectedRoute pour un scan QR a froid, cf.
  // getSafeRedirect ci-dessus) chaine a travers tout l'aller-retour WordPress - voir
  // handle_sso_login_request() et sso_wp_redirect() cote WordPress/backend, qui le relaient
  // jusqu'a la redirection finale vers /login?sso_code=...&redirect=...
  const currentRedirect = getSafeRedirect();
  const redirectQueryParam = currentRedirect !== '/' ? `&redirect=${encodeURIComponent(currentRedirect)}` : '';
  const wpSsoUrl = clubInfo.wp_site_url ? `${clubInfo.wp_site_url.replace(/\/+$/, '')}/wp-admin/admin-ajax.php?action=cesam_sso_login${redirectQueryParam}` : null;
  const ssoAvailable = !isTestMode && !!wpSsoUrl;
  const showForm = isTestMode || !ssoAvailable || showClassicForm;

  // Maintenance : on remplace la mire de connexion, on ne la double pas d'un bandeau. Un
  // adherent qui voit un formulaire le remplit - et ses ennuis commencent apres, sur des
  // donnees en cours de manipulation par le TNR.
  if (clubInfo.maintenance_mode && !bypassMaintenance) {
    return (
      <MaintenancePage
        clubInfo={clubInfo}
        version={APP_VERSION}
        onBypass={() => {
          try { sessionStorage.setItem('cesam_maintenance_bypass', '1'); } catch { /* sans persistance */ }
          setBypassMaintenance(true);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-cyan-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-sky-950 p-4 relative isolate">
      {/* 1.59.0.0 (demande du club) : fond anime sur l'ecran de connexion, tous themes - un
          degrade fixe sur telephone et si l'appareil demande de reduire les animations. */}
      <FondAnime style={{ position: 'absolute' }} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-sky-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative animate-fade-in shadow-2xl border-0 bg-white/80 backdrop-blur-xl">
        <CardHeader className="text-center pb-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={clubName}
              className="mx-auto w-20 h-20 object-contain rounded-2xl shadow-lg mb-4"
              data-testid="login-club-logo"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-sky-500/30 mb-4 ${logoUrl ? 'hidden' : ''}`}
            data-testid="login-default-icon">
            <Waves className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">
            {clubName}
          </CardTitle>
          <CardDescription className="text-base">
            Espace adhérent
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-5">
            {healthStatus && healthStatus.status === 'error' && (
              <Alert className="animate-fade-in border-amber-500 bg-amber-50 dark:bg-amber-950/30">
                <AlertCircle className="h-4 w-4 text-amber-700" />
                <AlertDescription className="text-amber-800 dark:text-amber-300 text-sm">
                  <strong>Problème serveur :</strong> {healthStatus.error || 'Base de donnees inaccessible'}
                  {healthStatus.database === 'disconnected' && <span className="block mt-1 text-xs">Verifiez que MySQL est demarre et accessible.</span>}
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant={errorType === 'auth' ? 'destructive' : undefined} className={`animate-fade-in ${errorType === 'db' ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30' : errorType === 'network' ? 'border-red-500 bg-red-50 dark:bg-red-950/30' : ''}`}>
                <AlertCircle className={`h-4 w-4 ${errorType === 'db' ? 'text-orange-700' : errorType === 'network' ? 'text-red-600' : ''}`} />
                <AlertDescription className={errorType !== 'auth' ? 'text-sm' : ''}>
                  {error}
                  {errorType === 'db' && <span className="block mt-1 text-xs text-orange-700 dark:text-orange-400">Le serveur ne peut pas accéder à la base de données. Contactez l'administrateur.</span>}
                  {errorType === 'network' && <span className="block mt-1 text-xs text-red-700 dark:text-red-400">Le backend ne repond pas. Verifiez que le serveur est en ligne.</span>}
                </AlertDescription>
              </Alert>
            )}

            {ssoAvailable && (
              <Button asChild className="w-full h-11 bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 shadow-lg shadow-sky-500/25" data-testid="login-sso-button">
                <a href={wpSsoUrl}>
                  <span className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />Se connecter avec votre compte du site
                  </span>
                </a>
              </Button>
            )}

            {showForm ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" placeholder="votre@email.fr"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required data-testid="login-email" className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password" type="password" placeholder="********"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required data-testid="login-password" className="h-11"
                  />
                  {/* 10/09/2026 (revue de securite "aucun flux de reinitialisation de mot de
                      passe") : le formulaire classique est le filet de securite si le SSO
                      WordPress tombe en panne - il lui faut un vrai moyen de definir un mot de
                      passe individuel, pas seulement de s'en souvenir. */}
                  <div className="text-right">
                    <Link to="/mot-de-passe-oublie" className="text-xs text-primary hover:text-primary" data-testid="login-forgot-password-link">
                      Mot de passe oublie ?
                    </Link>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant={ssoAvailable ? 'outline' : 'default'}
                  className={ssoAvailable ? 'w-full h-11' : 'w-full h-11 bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 shadow-lg shadow-sky-500/25'}
                  disabled={loading} data-testid="login-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connexion...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-4 h-4" />Se connecter
                    </span>
                  )}
                </Button>

                {ssoAvailable && (
                  <button
                    type="button"
                    onClick={() => setShowClassicForm(false)}
                    className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
                    data-testid="login-hide-classic-form"
                  >
                    Revenir a la connexion via le site
                  </button>
                )}
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowClassicForm(true)}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
                data-testid="login-show-classic-form"
              >
                <KeyRound className="w-3 h-3" />Se connecter avec un mot de passe
              </button>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {(clubInfo.app_mode || 'production') === 'test' && (
            <div className="mt-4 p-3 bg-muted rounded-lg text-xs text-left space-y-1">
              <p className="font-semibold text-foreground">Comptes de test :</p>
              <p><span className="font-mono bg-card px-1 rounded">webmaster@cesam-plongee.fr</span> / <span className="font-mono">admin123</span> <span className="text-purple-600">(Admin)</span></p>
              <p><span className="font-mono bg-card px-1 rounded">moniteur@cesam-plongee.fr</span> / <span className="font-mono">admin123</span> <span className="text-primary">(Encadrant)</span></p>
              <p><span className="font-mono bg-card px-1 rounded">secretariat@cesam-plongee.fr</span> / <span className="font-mono">admin123</span> <span className="text-rose-600">(Secretariat)</span></p>
              <p><span className="font-mono bg-card px-1 rounded">materiel@cesam-plongee.fr</span> / <span className="font-mono">admin123</span> <span className="text-amber-700">(Gest. Materiel)</span></p>
              <p><span className="font-mono bg-card px-1 rounded">marie.dupont@email.fr</span> / <span className="font-mono">admin123</span> <span className="text-emerald-700">(Adherent)</span></p>
            </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground font-mono" data-testid="login-version">{APP_VERSION}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
